import { useState } from "react"

import { Cell, Pie, PieChart, PieProps, ResponsiveContainer, Sector, SectorProps, Tooltip } from "recharts"

import { Status, useInsightPanelDataFetcher } from "./useInsightPanelDataFetcher"
import { Reaction, ReactionType } from "~/src/components/BeeKit/Reaction"
import {
  InsightsPanel,
  InsightsPanelContent,
  InsightsPanelErrorDefault,
  InsightsPanelHeader,
} from "~/src/components/Dashboard/InsightsPanel"
import { WithErrors } from "~/src/types/withErrors"

const COLORS = ["#3DB3E6", "#F59E0B", "#2B3E65", "#E80C55"] as const
const PIE_SIZE = "70%"
const HOVER_ZOOM_FACTOR = 1.05

export type RecipientReactionsData = WithErrors<{
  totalReactions: number
  reactions: {
    reaction: ReactionType
    total: number
  }[]
}>

export type StorefrontInsightsRecipientReactionsRenderPanelProps = {
  status: Status
  reactionsData: RecipientReactionsData | undefined
}

export function StorefrontInsightsRecipientReactionsPanel() {
  const { isLoading, data } = useInsightPanelDataFetcher<RecipientReactionsData>(`recipient_reactions`)

  let status: Status
  if (isLoading) {
    status = "loading"
  } else if (data?.errors) {
    status = "error"
  } else if (data?.reactions && data.reactions.length > 0) {
    status = "success"
  } else {
    status = "noData"
  }
  return <StorefrontInsightsRecipientReactionsRenderPanel status={status} reactionsData={data} />
}

export function StorefrontInsightsRecipientReactionsRenderPanel({
  status,
  reactionsData,
}: StorefrontInsightsRecipientReactionsRenderPanelProps) {
  return (
    <InsightsPanel className="@container h-full w-full">
      <InsightsPanelHeader title="Recipient reactions" />
      <InsightsPanelContent className="overflow-y-auto px-4 py-6">
        <div className="h-full">
          <RecipientReactionsContent status={status} data={reactionsData as RecipientReactionsData} />
        </div>
      </InsightsPanelContent>
    </InsightsPanel>
  )
}

function RecipientReactionsContent({ status, data }: { status: Status; data: RecipientReactionsData }) {
  if (status === "loading") return <RecipientReactionsSkeleton />
  if (status === "error") return <InsightsPanelErrorDefault />
  if (status === "noData") return <RecipientReactionsNoData />
  return <RecipientReactionsChart data={data} />
}

function RecipientReactionsChart({ data }: { data: RecipientReactionsData }) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const rows = data.reactions

  return (
    // 2-col layout: text on the left, chart on the right
    <div className="grid h-full grid-cols-1 @md:grid-cols-[1fr_2fr] @md:gap-6">
      <div className="flex flex-col justify-start">
        <p className="text-2xl font-medium text-gray-900">{data?.totalReactions.toLocaleString() ?? "0"}</p>
        <p className="text-nowrap text-gray-500">Reactions received</p>
      </div>

      <div className="flex h-full flex-col">
        <div className="mx-auto aspect-square w-full max-w-[360px] overflow-hidden @md:aspect-auto @md:max-w-none @md:flex-1">
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <PieChart className="**:outline-none">
              <Pie
                data={rows}
                cx="50%"
                cy="50%"
                outerRadius={PIE_SIZE}
                dataKey="total"
                stroke="none"
                labelLine={false}
                {...({
                  activeIndex,
                  activeShape: (p: SectorProps) => (
                    <Sector {...p} outerRadius={(p.outerRadius as number) * HOVER_ZOOM_FACTOR} />
                  ),
                } as Partial<PieProps>)}
                onMouseEnter={(_, idx) => setActiveIndex(idx)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {rows.map((entry) => {
                  const colorIndex = Object.values(ReactionType).indexOf(entry.reaction)
                  return <Cell key={`cell-${entry.reaction}`} fill={COLORS[colorIndex % COLORS.length]} />
                })}
              </Pie>

              <Tooltip cursor={false} wrapperStyle={{ outline: "none" }} content={<ReactionsTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <Legend />
      </div>
    </div>
  )
}

function ReactionsTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: Array<{ payload: { reaction: ReactionType; total: number } }>
}) {
  if (!active || !payload?.length) return null
  const { reaction, total } = payload[0].payload

  return (
    <div className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-gray-900 shadow-md ring-1 ring-black/5">
      <Reaction reaction={reaction} className="h-6 w-6" />
      <span className="text-base font-semibold">{total.toLocaleString()}</span>
    </div>
  )
}

function RecipientReactionsNoData() {
  return (
    <div className="grid h-full grid-cols-1 gap-6 @md:grid-cols-[1fr_2fr]">
      <div className="flex flex-col justify-start">
        <p className="text-2xl font-medium">0</p>
        <p className="text-nowrap text-gray-500">Reactions received</p>
      </div>

      <div className="flex h-full flex-col">
        <div className="mx-auto aspect-square w-full max-w-[360px] overflow-hidden @md:aspect-auto @md:max-w-none @md:flex-1">
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <PieChart data={[{ reaction: "No Data", total: 9999 }]} className="**:outline-none">
              <Pie dataKey="total" cx="50%" cy="50%" outerRadius={PIE_SIZE} fill="#e5e7eb" stroke="none" />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <Legend />
      </div>
    </div>
  )
}

function RecipientReactionsSkeleton() {
  const skeletonData = [{ reaction: "none", total: 9999 }]
  return (
    <div className="grid h-full animate-pulse grid-cols-1 gap-6 @md:grid-cols-[1fr_2fr]">
      <div className="flex flex-col justify-start gap-3 p-4">
        <div className="h-3 w-12 rounded-full bg-zinc-200" />
        <div className="h-3 w-24 rounded-full bg-zinc-200" />
      </div>

      <div className="flex h-full flex-col">
        <div className="mx-auto aspect-square w-full max-w-[360px] overflow-hidden @md:aspect-auto @md:max-w-none @md:flex-1">
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <PieChart data={skeletonData} className="**:outline-none">
              <Pie dataKey="total" cx="50%" cy="50%" outerRadius={PIE_SIZE} fill="#e5e7eb" stroke="none" />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

function Legend() {
  return (
    <div className="@container mt-3 flex flex-wrap items-center justify-between px-2">
      {Object.values(ReactionType).map((reaction, index) => {
        const color = COLORS[index % COLORS.length]
        return (
          <div key={`legend-${reaction}`} className="flex items-center gap-2">
            <span
              className="inline-block size-4 rounded-full @xs:size-6"
              style={{ backgroundColor: color }}
              aria-hidden
            />
            <Reaction reaction={reaction} className="size-4 @xs:size-6" />
          </div>
        )
      })}
    </div>
  )
}
